import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowDown, ArrowLeft, ArrowRight, ArrowUp, GripVertical, Plus, Trash2, UsersRound } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ErrorState, LoadingState } from "@/components/ui/StateBoundaries";
import { ConfirmDialog } from "@/components/admin/menu/shared";
import { groupByArea } from "@/components/admin/TableManager";
import { cacheBootstrapFromServer } from "@/db/offlineDb";
import * as layout from "@/lib/layoutApi";
import * as tablesApi from "@/lib/tablesApi";

/**
 * The floor designer (owner, 2026-10-25): the board itself, editable. What is laid
 * out here is exactly what the billing and CaptainPAD boards show — areas in order,
 * tables in order within them, each tile 1–2 columns wide and 1–2 rows tall.
 *
 * Arrangement (order, area, size) is staged and saved together in ONE all-or-nothing
 * request (PUT /floor/layout), so the floor is never half-rearranged. Adding or
 * removing a table is a server write of its own; it waits until the arrangement is
 * saved or discarded, and says so.
 *
 * Every drag has a tap-and-button equivalent (DESIGN.md §2.8; a gesture is never the
 * only way): select a tile, then move it, resize it or change its area from the bar.
 */
const MAX_SPAN = 2;
const SIZES = [[1, 1], [2, 1], [1, 2], [2, 2]];
const nameOf = t => t.label || `Table ${t.number}`;
const failure = (e, fallback) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string" && d) return d;
  if (Array.isArray(d) && d.length) return d.map(x => x?.msg || String(x)).join("; ");
  return fallback;
};
const span = v => Math.max(1, Math.min(MAX_SPAN, Number(v) || 1));

/** The save payload: every table's place (10, 20, …), area and size. */
export const arrangementPayload = areas => {
  let n = 0;
  return areas.flatMap(area => area.tables.map(t => ({
    // The stored size is sent as-is (a legacy 3 stays 3); the boards clamp to 2.
    id: t.id, sort_order: (n += 10), floor: area.name, width: Number(t.width) || 1, height: Number(t.height) || 1,
  })));
};

/** Move table `id` to sit before `beforeId` in area `toArea` (null = end of that area). */
export const moveTable = (areas, id, toArea, beforeId = null) => {
  let moving = null;
  const next = areas.map(a => ({ ...a, tables: a.tables.filter(t => (t.id === id ? ((moving = t), false) : true)) }));
  if (!moving) return areas;
  const target = next.find(a => a.name === toArea);
  if (!target) return areas;
  const at = beforeId == null ? -1 : target.tables.findIndex(t => t.id === beforeId);
  target.tables.splice(at < 0 ? target.tables.length : at, 0, moving);
  return next.filter(a => a.tables.length);
};

export const moveArea = (areas, name, toIndex) => {
  const from = areas.findIndex(a => a.name === name);
  if (from < 0 || toIndex < 0 || toIndex >= areas.length || from === toIndex) return areas;
  const next = [...areas];
  const [area] = next.splice(from, 1);
  next.splice(toIndex, 0, area);
  return next;
};

export default function FloorBoardDesigner({ active = true }) {
  const { token } = usePOS();
  const [saved, setSaved] = useState(null);     // areas as the server has them
  const [areas, setAreas] = useState(null);     // the draft
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null);
  const [newArea, setNewArea] = useState("");
  const [removing, setRemoving] = useState(null);
  const [drag, setDrag] = useState(null);       // { kind: "table"|"area", id, x, y, label }
  const [over, setOver] = useState(null);       // { area, before } | { areaIndex }
  const pressRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const rows = await layout.getLayout(token);
      const grouped = groupByArea(rows);
      setSaved(grouped);
      setAreas(grouped);
      setError(null);
      return grouped;
    } catch (e) {
      const message = failure(e, "Could not read the floor");
      // A failed re-read after the floor is already on screen must not blank it.
      setAreas(current => { if (current) toast.error(message); else setError(message); return current; });
      return null;
    }
  }, [token]);
  const dirty = useMemo(
    () => !!saved && JSON.stringify(arrangementPayload(saved)) !== JSON.stringify(arrangementPayload(areas || [])),
    [saved, areas]
  );
  // Read the floor when shown — the Tables and Areas pages change it too — but never
  // over an arrangement that is still being edited.
  const dirtyRef = useRef(false);
  dirtyRef.current = dirty;
  useEffect(() => { if (active && !dirtyRef.current) load(); }, [active, load]);
  const allTables = useMemo(() => (areas || []).flatMap(a => a.tables), [areas]);
  const sel = allTables.find(t => t.id === selected) || null;
  const selArea = sel ? areas.find(a => a.tables.some(t => t.id === sel.id)) : null;

  const refreshBoards = () => cacheBootstrapFromServer(token).catch(() => {});

  const save = async () => {
    setBusy(true);
    try {
      // The save rewrites every table's place, so it must start from the floor as it
      // is NOW: if another screen (the Tables page, another terminal) changed it since
      // this draft began, saving would silently undo that. Refuse instead.
      const fresh = groupByArea(await layout.getLayout(token));
      if (JSON.stringify(arrangementPayload(fresh)) !== JSON.stringify(arrangementPayload(saved))) {
        toast.error("The floor was changed on another screen while you were arranging it. Nothing was saved. Discard to load the latest floor, then redo the move.");
        return;
      }
      await layout.saveLayout(arrangementPayload(areas), token);
      toast.success("Floor saved — every board now shows this arrangement");
      await load();
      refreshBoards();
    } catch (e) {
      toast.error(failure(e, "The floor was not saved. Nothing changed."));
    } finally {
      setBusy(false);
    }
  };

  const addTable = async areaName => {
    const number = Math.max(0, ...allTables.map(t => t.number)) + 1;
    const last = areas.find(a => a.name === areaName)?.tables.at(-1);
    setBusy(true);
    try {
      await tablesApi.createTable({
        number, label: `${number} ${areaName}`, floor: areaName, capacity: 4,
        sort_order: (last?.sort_order ?? allTables.length * 10) + 5,
      }, token);
      toast.success(`Table ${number} added to ${areaName}`);
      await load();
      refreshBoards();
    } catch (e) {
      toast.error(failure(e, "The table was not added"));
    } finally {
      setBusy(false);
    }
  };

  const addArea = () => {
    const name = newArea.trim();
    if (!name) return;
    if (areas.some(a => a.name.toLowerCase() === name.toLowerCase())) { toast.error(`There is already an area called ${name}`); return; }
    setNewArea("");
    // An area exists through its tables: it is created with its first one.
    addTable(name);
  };

  const remove = async table => {
    setRemoving(null);
    setBusy(true);
    try {
      await tablesApi.deleteTable(table.id, token);
      toast.success(`${nameOf(table)} removed`);
      setSelected(null);
      await load();
      refreshBoards();
    } catch (e) {
      toast.error(failure(e, "The table was not removed"));
    } finally {
      setBusy(false);
    }
  };

  const update = fn => setAreas(current => fn(current));
  const resize = (w, h) => update(cur => cur.map(a => ({ ...a, tables: a.tables.map(t => (t.id === sel.id ? { ...t, width: w, height: h } : t)) })));
  const nudge = by => {
    const list = selArea.tables;
    const i = list.findIndex(t => t.id === sel.id);
    const j = i + by;
    if (j < 0 || j >= list.length) return;
    const before = by < 0 ? list[j].id : list[j + 1]?.id ?? null;
    update(cur => moveTable(cur, sel.id, selArea.name, before));
  };

  // -- pointer drag (mouse and touch) -----------------------------------------
  const hit = (x, y) => {
    const el = document.elementFromPoint(x, y);
    const tile = el?.closest?.("[data-designer-tile]");
    const section = el?.closest?.("[data-designer-area]");
    return { tileId: tile ? Number(tile.dataset.designerTile) : null, area: section?.dataset.designerArea ?? null };
  };
  const onPointerDown = (e, kind, id, label) => {
    // One finger, main button: a second finger or a right-click is not a drag.
    if (busy || !e.isPrimary || e.button !== 0 || pressRef.current) return;
    pressRef.current = { kind, id, label, x: e.clientX, y: e.clientY, lastY: e.clientY, started: false };
  };
  useEffect(() => {
    const move = e => {
      const p = pressRef.current;
      if (!p) return;
      if (!p.started && Math.hypot(e.clientX - p.x, e.clientY - p.y) < 8) return;
      e.preventDefault();
      p.lastY = e.clientY;
      if (!p.started) {
        p.started = true;
        // Near the top or bottom edge the page keeps scrolling while the pointer is
        // held there, so a table or area can be carried past one screen.
        let el = document.querySelector("[data-testid=floor-board-designer]");
        while (el && !(el.scrollHeight > el.clientHeight && /(auto|scroll)/.test(getComputedStyle(el).overflowY))) el = el.parentElement;
        const scroller = el || document.scrollingElement;
        p.timer = window.setInterval(() => {
          const box = scroller === document.scrollingElement ? { top: 0, bottom: window.innerHeight } : scroller.getBoundingClientRect();
          if (p.lastY < box.top + 72) scroller.scrollBy(0, -18);
          else if (p.lastY > box.bottom - 72) scroller.scrollBy(0, 18);
        }, 30);
      }
      setDrag({ kind: p.kind, id: p.id, label: p.label, x: e.clientX, y: e.clientY });
      const h = hit(e.clientX, e.clientY);
      if (p.kind === "table") setOver(h.area ? { area: h.area, before: h.tileId !== p.id ? h.tileId : null } : null);
      else setOver(h.area ? { area: h.area } : null);
    };
    const end = (e, drop) => {
      const p = pressRef.current;
      if (!p || (e.pointerId !== undefined && e.isPrimary === false)) return;
      pressRef.current = null;
      window.clearInterval(p.timer);
      if (!p.started) { if (drop && p.kind === "table") setSelected(s => (s === p.id ? null : p.id)); return; }
      const h = drop ? hit(e.clientX, e.clientY) : {};
      // Released on itself (an aborted drag): nothing moves.
      if (p.kind === "table" && h.area && h.tileId !== p.id) update(cur => moveTable(cur, p.id, h.area, h.tileId));
      if (p.kind === "area" && h.area) update(cur => moveArea(cur, p.id, cur.findIndex(a => a.name === h.area)));
      setDrag(null);
      setOver(null);
    };
    window.addEventListener("pointermove", move, { passive: false });
    const up = e => end(e, true);
    const cancel = e => end(e, false);
    window.addEventListener("pointerup", up);
    window.addEventListener("pointercancel", cancel);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      window.removeEventListener("pointercancel", cancel);
      window.clearInterval(pressRef.current?.timer);
    };
  }, []);

  if (error) return <ErrorState message={error} onRetry={load} testId="floor-designer-error" />;
  if (!areas) return <LoadingState message="Reading the floor…" testId="floor-designer-loading" />;

  const writesBlocked = busy || dirty;
  const blockedWhy = dirty ? "Save or discard the arrangement first" : undefined;

  return (
    <section className="space-y-base" data-testid="floor-board-designer">
      <header className="flex flex-wrap items-end justify-between gap-snug">
        <div>
          <h3 className="text-body font-semibold">Floor designer</h3>
          <p className="text-meta text-muted-foreground">
            Drag tables and areas, or tap a table for size, order and area. The boards show tables in this order and at these sizes; how many fit on a row depends on each screen.
          </p>
        </div>
        <div className="flex items-center gap-tight">
          <Input value={newArea} onChange={e => setNewArea(e.target.value)} maxLength={30} placeholder="New area name" className="h-8 w-44" data-testid="floor-designer-new-area" />
          <Button size="sm" variant="outline" onClick={addArea} disabled={writesBlocked || !newArea.trim()} title={blockedWhy} data-testid="floor-designer-add-area">
            <Plus className="w-3.5 h-3.5" /> Add area
          </Button>
        </div>
      </header>

      {areas.map((area, areaIndex) => (
        <div
          key={area.name}
          data-designer-area={area.name}
          className={`rounded-lg border bg-card p-snug space-y-snug ${over?.area === area.name && drag ? "border-primary bg-info-subtle" : ""} ${drag?.kind === "area" && drag.id === area.name ? "opacity-50" : ""}`}
          data-testid={`floor-designer-area-${area.name}`}
        >
          <div className="flex items-center gap-tight">
            <button
              type="button"
              className="h-8 w-8 flex items-center justify-center rounded text-muted-foreground hover:bg-muted cursor-grab touch-none"
              onPointerDown={e => onPointerDown(e, "area", area.name, area.name)}
              aria-label={`Drag ${area.name}`}
              data-testid={`floor-designer-area-${area.name}-handle`}
            >
              <GripVertical className="w-4 h-4" />
            </button>
            <h4 className="text-body font-semibold">{area.name}</h4>
            <span className="text-meta text-muted-foreground">{area.tables.length} tables</span>
            <div className="ml-auto flex items-center gap-tight">
              <Button size="sm" variant="ghost" onClick={() => update(cur => moveArea(cur, area.name, areaIndex - 1))} disabled={busy || areaIndex === 0} aria-label={`Move ${area.name} up`} data-testid={`floor-designer-area-${area.name}-up`}><ArrowUp className="w-4 h-4" /></Button>
              <Button size="sm" variant="ghost" onClick={() => update(cur => moveArea(cur, area.name, areaIndex + 1))} disabled={busy || areaIndex === areas.length - 1} aria-label={`Move ${area.name} down`} data-testid={`floor-designer-area-${area.name}-down`}><ArrowDown className="w-4 h-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => addTable(area.name)} disabled={writesBlocked} title={blockedWhy} data-testid={`floor-designer-area-${area.name}-add`}>
                <Plus className="w-3.5 h-3.5" /> Table
              </Button>
            </div>
          </div>

          {/* The board's own grid: same columns, dense flow and row height. */}
          <div className="grid grid-cols-[repeat(auto-fill,minmax(8rem,1fr))] auto-rows-[8.5rem] grid-flow-dense gap-2">
            {area.tables.map(t => {
              const isSel = selected === t.id;
              const isTarget = drag?.kind === "table" && over?.area === area.name && over?.before === t.id;
              return (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  data-designer-tile={t.id}
                  onPointerDown={e => onPointerDown(e, "table", t.id, nameOf(t))}
                  onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setSelected(s => (s === t.id ? null : t.id)); } }}
                  aria-pressed={isSel}
                  aria-label={`${nameOf(t)}, ${span(t.width)} by ${span(t.height)}`}
                  style={{ gridColumn: `span ${span(t.width)}`, gridRow: `span ${span(t.height)}` }}
                  className={`relative flex flex-col items-center justify-center rounded-lg border bg-card select-none touch-none cursor-grab focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/25
                    ${isSel ? "border-2 border-primary bg-info-subtle" : ""} ${isTarget ? "ring-2 ring-primary ring-offset-2" : ""} ${drag?.kind === "table" && drag.id === t.id ? "opacity-40" : ""}`}
                  data-testid={`floor-designer-table-${t.id}`}
                >
                  <span className="absolute top-1.5 right-2 flex items-center gap-0.5 text-meta text-muted-foreground tabular-nums">
                    <UsersRound className="w-3.5 h-3.5" />{t.capacity}
                  </span>
                  <strong className="text-lede font-semibold leading-tight w-min text-center px-1">{nameOf(t)}</strong>
                  {(span(t.width) > 1 || span(t.height) > 1) && <span className="mt-0.5 text-micro text-muted-foreground">{span(t.width)}×{span(t.height)}</span>}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {!areas.length && <p className="text-body text-muted-foreground" data-testid="floor-designer-empty">No tables yet. Name an area above and add it.</p>}

      {drag && (
        <div className="fixed z-50 pointer-events-none rounded-lg border-2 border-primary bg-card px-3 py-2 text-body font-semibold shadow-md"
          style={{ left: drag.x + 12, top: drag.y + 12 }}>
          {drag.label}
        </div>
      )}

      {/* Selection and save bar: pinned, one primary (Save). */}
      <div className="sticky bottom-0 z-30 rounded-lg border bg-card shadow-sm px-base py-snug flex flex-wrap items-center gap-snug" data-testid="floor-designer-bar">
        {sel ? (
          <>
            <strong className="text-body">{nameOf(sel)}</strong>
            <span className="text-meta text-muted-foreground">Size</span>
            {SIZES.map(([w, h]) => (
              <Button key={`${w}x${h}`} size="sm" variant={span(sel.width) === w && span(sel.height) === h ? "default" : "outline"} onClick={() => resize(w, h)} disabled={busy} data-testid={`floor-designer-size-${w}x${h}`}>
                {w}×{h}
              </Button>
            ))}
            <Button size="sm" variant="ghost" onClick={() => nudge(-1)} disabled={busy} aria-label="Move earlier" data-testid="floor-designer-earlier"><ArrowLeft className="w-4 h-4" /></Button>
            <Button size="sm" variant="ghost" onClick={() => nudge(1)} disabled={busy} aria-label="Move later" data-testid="floor-designer-later"><ArrowRight className="w-4 h-4" /></Button>
            <select
              value={selArea?.name}
              onChange={e => update(cur => moveTable(cur, sel.id, e.target.value, null))}
              disabled={busy}
              className="h-8 rounded-md border bg-background px-2 text-body"
              aria-label="Area"
              data-testid="floor-designer-area-select"
            >
              {areas.map(a => <option key={a.name} value={a.name}>{a.name}</option>)}
            </select>
            <Button size="sm" variant="ghost" className="text-danger" onClick={() => setRemoving(sel)} disabled={writesBlocked} title={blockedWhy} data-testid="floor-designer-remove">
              <Trash2 className="w-4 h-4" /> Remove
            </Button>
          </>
        ) : (
          <span className="text-meta text-muted-foreground">{dirty ? "Unsaved arrangement" : "Tap a table to resize, reorder or move it"}</span>
        )}
        <div className="ml-auto flex items-center gap-tight">
          {dirty && <span className="text-meta text-warning" data-testid="floor-designer-dirty">Not saved yet</span>}
          <Button size="sm" variant="outline" onClick={() => { setAreas(saved); setSelected(null); load(); }} disabled={busy || !dirty} data-testid="floor-designer-discard">Discard</Button>
          <Button size="sm" variant="action" onClick={save} disabled={busy || !dirty} data-testid="floor-designer-save">
            {busy ? "Saving…" : "Save floor"}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={Boolean(removing)}
        title={`Remove ${removing ? nameOf(removing) : ""}?`}
        body="It disappears from every board. Old bills keep their table. A table with a running order cannot be removed."
        confirmLabel="Remove table"
        onConfirm={() => remove(removing)}
        onCancel={() => setRemoving(null)}
        testId="floor-designer-remove-dialog"
      />
    </section>
  );
}
